import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import {
  assertVoiceStopDurable,
  useSpeechToText,
  type CaptureDurableNotice,
  type SpeechSessionOutcome,
  type SpeechStartOptions,
  type VoiceStopResult,
} from './useSpeechToText';
import type { ScriptureAnchor } from '@/types/prayer';
import VoiceDisclosure from '@/components/VoiceDisclosure';
import VoiceErrorToast from '@/components/VoiceErrorToast';
import AutoStopToast from '@/components/AutoStopToast';
import VoiceStartUnavailableToast from '@/components/VoiceStartUnavailableToast';
import { useAudioQueueStore } from '@/lib/audio-queue-store';
import {
  clearAwaitingInlineSaveOrphans,
  discardInterruptedRecording,
  discardLatestAwaitingInlineSaveOrphan,
  homeInlineSaveOffer,
  inlineSaveOfferFailedWithoutWords,
  newestInlineSaveOffer,
} from '@/lib/audio-queue';
import { isCoreReadOnly } from '@/lib/core-cutover';
import {
  loadBrowserInterruptedInterim,
  saveBrowserInterruptedInterim,
  clearBrowserInterruptedInterim,
} from '@/lib/interrupted-session';
import { hasActiveVoiceCaptureWork } from '@/lib/voice-work-fence';
import {
  browserCapturesAwaitingText,
  commitCapture,
  deliverCaptureText,
  endCaptureWithoutPayload,
  isCustodyAdmissionBusy,
  retireRuntimeCaptures,
  retryFailedCaptures,
  subscribeCustody,
  unsavedRecordingState,
} from '@/lib/voice-capture-custody';
import {
  isOwnerTransitionBarrierActive,
  registerOwnerTransitionDrain,
} from '@/lib/owner-transition';
import {
  cloneScriptureAnchor,
  parseScriptureAnchor,
} from '@/lib/scripture-anchor';
import { useUpdateHold } from '@/hooks/useUpdateHold';
import { VOICE_HOLD_REASON } from '@/lib/update-safety';

interface TargetHandlers {
  onFinal: (text: string, queueItemId?: string) => void;
  getRecoveryText?: () => string;
  /**
   * C2 (testimony plan rev 4, section 8.5): the capture this target started
   * is durable on this device. Constant-time, synchronous work only; no
   * storage, network or lock calls.
   */
  onCaptureDurable?: (notice: CaptureDurableNotice) => void;
}

/** Synchronous view of the current capture (section 8.4 terms). */
export interface VoiceCaptureState {
  /** The target that started the current, not yet finished capture. */
  target: string | null;
  captureToken?: string;
  /** GENUINELY LISTENING: recording, and no stop or finalization has begun. */
  listening: boolean;
  /** STOP UNDERWAY: a stop or interruption finalization has begun. */
  stopping: boolean;
}

/** One accepted capture, from its start until everything about it finished. */
interface CaptureRecord {
  id: number;
  target: string | null;
  token?: string;
  speechSeq: number;
  settled: boolean;
  /** cancel() owns this capture's end: it keeps today's cancel semantics. */
  cancelled: boolean;
}

/**
 * Interrupted-session entries surfaced via the resume card. Stack
 * semantics per E.12 ruling: most-recent first, dismiss pops the head.
 *
 * Computed (not stored) from two sources:
 *   - audio-queue store (any item with prayerId === null is by
 *     definition an enhanced-mode interrupted session)
 *   - localStorage `adia-interrupted-interim` (browser-mode captured
 *     text)
 */
export type InterruptedSession =
  | {
      id: string;
      mode: 'enhanced';
      queueItemId: string;
      capturedAt: string;
      scriptureAnchor?: ScriptureAnchor;
    }
  | {
      id: string;
      mode: 'browser';
      queueItemId?: string;
      capturedText: string;
      capturedAt: string;
      scriptureAnchor?: ScriptureAnchor;
    };

interface VoiceCaptureValue {
  target: string | null;
  setTarget: (target: string | null) => void;
  interim: string;
  isRecording: boolean;
  isTranscribing: boolean;
  isSupported: boolean;
  /** Read-only: a stop or interruption finalization is underway. */
  isStopping: boolean;
  /**
   * R4: true while a new capture would be refused (one is starting,
   * recording, or still settling). Reactive mirror of isAdmissionBusy().
   */
  admissionBusy: boolean;
  isAdmissionBusy: () => boolean;
  /** Increments on every refused start; lets a mic show busy feedback. */
  getRefusalCount: () => number;
  getCaptureState: () => VoiceCaptureState;
  /**
   * Returns false when the start was refused (nothing changed), true when it
   * was accepted or is waiting on the first-use disclosure.
   */
  start: (
    target?: string,
    opts?: SpeechStartOptions,
  ) => boolean;
  /** Dismiss only this exact request while it is waiting on disclosure. */
  cancelPendingStart: (target: string, captureToken: string) => boolean;
  stop: () => Promise<void>;
  cancel: () => Promise<void>;
  registerTarget: (name: string, handlers: TargetHandlers) => () => void;
  /**
   * Returns the current normalized audio level (0..1) in enhanced mode,
   * or null when no analyser is wired (browser STT mode, idle, or pre-
   * data window). AudioMeter polls this per animation frame to drive
   * bar heights; returning a getter (rather than a reactive value)
   * keeps re-renders out of the hot path.
   */
  getLevel: () => number | null;
  /**
   * Stack of pending resume-card entries. Most-recent-first. The
   * resume card renders the head (`interruptedSessions[0]`); on
   * dismiss the head pops and the card auto-rerenders for the next
   * entry until the stack is empty. See E.12 ruling. Recordings set
   * aside with `deferInterruption` are not in it (RU item 3), so the card
   * and `dismissInterruption` always mean the same head.
   */
  interruptedSessions: InterruptedSession[];
  /**
   * RU item 3 (2026-10-20): the interrupted recordings the resume card's
   * "Not now" set aside in this page (most recent first). Home lists them in
   * an entry; nothing about them is persisted. `wordsMoved` (fix round 1):
   * its words already reached the Home draft before its close was refused, so
   * Home must not invite saving the recording again.
   */
  deferredInterruptions: ReadonlyArray<{ session: InterruptedSession; wordsMoved: boolean }>;
  /**
   * RU item 3 (2026-10-20): "Not now" on the resume card, after "Save what I
   * have" was refused because the recording's bytes cannot be read in this
   * page (where Save and Discard are both refused). Takes the exact session
   * out of `interruptedSessions` for the rest of this page and this owner's
   * session: page memory only, never persisted, and it never dequeues,
   * saves, clears a recovery record or reports success. Only a durable
   * recording (an enhanced session with its queue row) can be set aside.
   * `wordsMoved` (fix round 1) records that its words already reached the
   * Home draft. Returns whether it was set aside.
   */
  deferInterruption: (sessionId: string, options?: { wordsMoved?: boolean }) => boolean;
  /**
   * Drop the head session. Used by all resume-card actions:
   *   - "Continue recording" → after pre-loading text into PrayPage
   *   - "Save what I have" → after creating the pending Prayer
   *   - "Discard" → straight dismiss
   *   - "Keep typing" (browser) → after pre-loading textarea
   *
   * For enhanced sessions: dequeues the audio from IDB. For browser
   * sessions: clears the localStorage key. Both side effects are
   * encoded here so callers don't have to remember the cleanup.
   */
  dismissInterruption: () => Promise<boolean>;
  /**
   * V.1.1: true when an awaiting-inline-save orphan is parked in
   * IDB. PrayPage's Save-gate uses this to keep the button visible
   * when the user has no typed text but has audio waiting for the
   * inline-save rescue path. Reactive over the audio-queue store so
   * the gate updates the moment a transcribe-fail enqueues — and
   * survives a reload because the orphan is on disk.
   */
  inlineSaveAvailable: boolean;
  /**
   * Mic repair F2 (2026-10-20): Home's offer exists AND may be saved as a
   * prayer. False for a terminal failed recording without words
   * (audio-queue.ts inlineSaveOfferFailedWithoutWords): Home then shows its banner
   * with Discard only, never Save.
   */
  inlineSaveSavable: boolean;
  /**
   * V.1.1: claim the most-recent awaiting-inline-save orphan and
   * return its queue item id. Caller creates a Prayer with
   * pendingTranscriptionId = queueItemId, then uses
   * createPendingPrayerFromAudio for the atomic write that saves the
   * prayer, binds prayerId, and clears awaitingInlineSave. Returns null when no orphan
   * exists. Does NOT mutate the row — the link call does.
   */
  claimPendingInlineSave: (
    scriptureAnchor?: ScriptureAnchor,
  ) => Promise<{ queueItemId: string } | null>;
  /**
   * V.1.1: drop any parked awaiting-inline-save orphan from IDB.
   * Called by PrayPage's normal-success path after a typed-text
   * save commits so the Save-gate doesn't carry stale rescue state
   * into the next compose.
   *
   * Source-scoped (bookmarks Phase 2): the caller passes its own
   * surface so a prayer-surface clear never wipes a bookmark orphan
   * and vice versa.
   */
  clearPendingInlineSave: (source: 'prayer' | 'bookmark') => Promise<string[]>;
}

const VoiceCaptureContext = createContext<VoiceCaptureValue | null>(null);

// Gate key for the first-tap voice disclosure. Keep the established key so
// owner-profile inspection does not see a new unknown `adia-*` record. The
// VALUE is versioned: users who saw the older provider-specific copy see the
// corrected dual-path disclosure once.
const DISCLOSURE_KEY = 'adia-voice-disclosure-seen';
const DISCLOSURE_VERSION = '2';

function snapshotStartOptions(
  opts?: SpeechStartOptions,
): SpeechStartOptions | undefined {
  if (!opts) return undefined;
  return {
    ...opts,
    recoveryDestination: opts.recoveryDestination
      ? { ...opts.recoveryDestination }
      : undefined,
    scriptureAnchor: opts.scriptureAnchor
      ? cloneScriptureAnchor(opts.scriptureAnchor)
      : undefined,
  };
}

// Module-scoped cache so we read localStorage once per page load rather
// than on every mic tap. Also the single source of truth in private-mode
// Safari where localStorage throws: once we've shown the modal in-session
// we won't re-show it on subsequent taps in the same session.
let disclosureSeenCached: boolean | null = null;

function hasSeenVoiceDisclosure(): boolean {
  if (disclosureSeenCached !== null) return disclosureSeenCached;
  try {
    disclosureSeenCached =
      localStorage.getItem(DISCLOSURE_KEY) === DISCLOSURE_VERSION;
  } catch {
    // Private-mode / disabled-storage: start un-seen, mark in-memory on
    // first show so a single session doesn't re-prompt.
    disclosureSeenCached = false;
  }
  return disclosureSeenCached;
}

function markVoiceDisclosureSeen() {
  disclosureSeenCached = true;
  try {
    localStorage.setItem(DISCLOSURE_KEY, DISCLOSURE_VERSION);
  } catch {
    // Storage unavailable — in-memory cache above is still updated.
  }
}

export function VoiceCaptureProvider({
  children,
  ownerId,
}: {
  children: ReactNode;
  ownerId?: string;
}) {
  // One id per genuine provider instance (minted in a state initializer, so
  // StrictMode's effect replay and rerenders keep it): custody records its
  // captures under it, and the one automatic retry on mount is keyed by it
  // (account-change recording plan rev 4.1, section 3.6).
  const [custodyRuntimeId] = useState(() => crypto.randomUUID());
  const tearingDownRef = useRef(false);
  const custodyBusy = useSyncExternalStore(subscribeCustody, isCustodyAdmissionBusy);
  const [target, setTargetState] = useState<string | null>(null);
  const [interim, setInterim] = useState('');
  const [disclosureOpen, setDisclosureOpen] = useState(false);
  const targetRef = useRef<string | null>(null);
  const targetsRef = useRef<Map<string, TargetHandlers>>(new Map());
  // Pending start captured at `start()` time while the disclosure modal
  // is open. Consumed on confirm so the post-modal recording routes to
  // the same surface the user originally tapped from. Stores BOTH the
  // target name AND the opts (bookmarks Phase 2) so the first-ever
  // bookmark recording keeps its inlineSaveSource through the disclosure
  // instead of defaulting to 'prayer'.
  const pendingStartRef = useRef<{
    name: string | null;
    opts?: SpeechStartOptions;
  } | null>(null);
  const activeStartOptionsRef = useRef<SpeechStartOptions | undefined>(undefined);

  const setTarget = useCallback((next: string | null) => {
    targetRef.current = next;
    setTargetState(next);
  }, []);

  const interruptionPersistenceRef = useRef<Promise<void> | null>(null);
  const interruptionPersistenceFailureRef = useRef<Error | null>(null);
  // R4 (testimony plan rev 4, section 8.7): the current capture, from its
  // accepted start until every part of its end has run. Admission waits on
  // it, and every late continuation checks it, so an earlier capture can never
  // clear a newer capture's target, options, error or active-work fence.
  const captureRef = useRef<CaptureRecord | null>(null);
  const captureIdRef = useRef(0);
  const refusalCountRef = useRef(0);
  const [captureBusy, setCaptureBusy] = useState(false);
  const [interruptionInFlight, setInterruptionInFlight] = useState(false);

  // Finishes one capture exactly once. A failed durable write is latched and
  // keeps the active-work fence set (rule #90), except for cancel(), which
  // keeps its historical "drop and clear" semantics.
  const finishCapture = useCallback((
    record: CaptureRecord,
    result: VoiceStopResult | null,
  ) => {
    if (captureRef.current !== record || record.settled) return;
    record.settled = true;
    // The work fence itself is custody's: a failed capture keeps its record
    // (and so the fence) until a retry makes it durable.
    if (result?.durability === 'failed' && !record.cancelled) {
      interruptionPersistenceFailureRef.current = result.error;
    }
    activeStartOptionsRef.current = undefined;
    if (record.target !== null && targetRef.current === record.target) {
      targetRef.current = null;
      setTargetState(null);
      setInterim('');
    }
    setCaptureBusy(false);
  }, []);

  const handleFinal = useCallback((finalText: string, queueItemId?: string) => {
    const current = targetRef.current;
    if (!current) return;
    // Deliver only to the editor that started this capture and is still open
    // on it; otherwise the words stay on the capture's durable row.
    const record = captureRef.current;
    if (record && record.target !== null && record.target !== current) return;
    const handlers = targetsRef.current.get(current);
    handlers?.onFinal(finalText, queueItemId);
  }, []);

  const handleInterim = useCallback((interimText: string) => {
    setInterim(interimText);
  }, []);

  const handleCaptureDurable = useCallback((notice: CaptureDurableNotice) => {
    const record = captureRef.current;
    if (
      !record ||
      record.target === null ||
      !notice.captureToken ||
      record.token !== notice.captureToken
    ) return;
    try {
      targetsRef.current.get(record.target)?.onCaptureDurable?.(notice);
    } catch {
      // A destination's handler can never reach the capture pipeline.
    }
  }, []);

  const handleSessionSettled = useCallback((
    sessionSeq: number,
    outcome: SpeechSessionOutcome,
  ) => {
    const record = captureRef.current;
    if (!record || record.speechSeq !== sessionSeq || record.settled) return;
    // An interruption's end is its persistence, settled by that promise.
    if (outcome.kind === 'interrupted') return;
    finishCapture(record, outcome.kind === 'stopped' ? outcome.result : null);
  }, [finishCapture]);

  const {
    isListening,
    isTranscribing,
    isSupported,
    isStopping: speechIsStopping,
    error,
    clearError,
    startListening,
    getCaptureActivity,
    stopListening,
    cancelListening,
    getLevel,
    finalizeForInterruption,
  } = useSpeechToText({
    onInterim: handleInterim,
    onFinal: handleFinal,
    onCaptureDurable: handleCaptureDurable,
    onSessionSettled: handleSessionSettled,
    custodyRuntimeId,
  });

  // Ref mirrors so the visibility handler reads the freshest state
  // without re-binding listeners on every interim word or isListening
  // transition.
  const isListeningRef = useRef(false);
  const isTranscribingRef = useRef(false);
  const interimRef = useRef('');
  useEffect(() => { isListeningRef.current = isListening; }, [isListening]);
  useEffect(() => { isTranscribingRef.current = isTranscribing; }, [isTranscribing]);
  useEffect(() => { interimRef.current = interim; }, [interim]);

  // App-update safety: never reload the app while recording or transcribing.
  useUpdateHold(isListening || isTranscribing, VOICE_HOLD_REASON);

  // ---- B.2 interrupted-session state ----
  // Browser mode retains the historical single localStorage recovery slot.
  // Enhanced sessions are derived from queue items with prayerId === null.
  // Lazy-init from localStorage so the hydration read runs ONCE on
  // first render. Avoids the react-hooks/set-state-in-effect lint
  // (calling setState in a useEffect body cascades renders).
  const [browserInterim, setBrowserInterim] = useState(
    () => loadBrowserInterruptedInterim(),
  );
  const queueItems = useAudioQueueStore((s) => s.items);

  const allInterruptedSessions = useMemo<InterruptedSession[]>(() => {
    const sessions: InterruptedSession[] = [];
    // Enhanced sessions: orphaned queue items, most recent first per
    // E.12 stack semantics. V.1.1: exclude rows flagged
    // awaitingInlineSave — those are transcribe-fail-on-stop orphans
    // claimed by PrayPage's Save button, not interrupted-session
    // orphans claimed by the resume card.
    const orphans = queueItems
      .filter(
        (i) =>
          i.prayerId === null &&
          !i.awaitingInlineSave &&
          i.ownerId === ownerId &&
          i.audioProtocolVersion === 1 &&
          i.localDisposition === 'unresolved' &&
          (i.destinationState === undefined || i.destinationState === 'unresolved') &&
          i.recoveryKind === 'new-prayer' &&
          (i.captureMode === 'enhanced' ||
            (i.captureMode === 'browser' && Boolean(i.transcribedText?.trim()))),
      )
      .sort((a, b) => b.capturedAt.localeCompare(a.capturedAt));
    for (const item of orphans) {
      const scriptureAnchor = item.scriptureAnchor
        ? parseScriptureAnchor(item.scriptureAnchor) ?? undefined
        : undefined;
      if (item.captureMode === 'browser' && item.transcribedText?.trim()) {
        sessions.push({
          id: item.id,
          mode: 'browser',
          queueItemId: item.id,
          capturedText: item.transcribedText.trim(),
          capturedAt: item.capturedAt,
          scriptureAnchor,
        });
      } else {
        sessions.push({
          id: item.id,
          mode: 'enhanced',
          queueItemId: item.id,
          capturedAt: item.capturedAt,
          scriptureAnchor,
        });
      }
    }
    if (browserInterim) {
      sessions.push({
        id: 'browser-interrupt',
        mode: 'browser',
        capturedText: browserInterim.text,
        capturedAt: browserInterim.capturedAt,
        scriptureAnchor: browserInterim.scriptureAnchor,
      });
    }
    return sessions.sort((a, b) => b.capturedAt.localeCompare(a.capturedAt));
  }, [queueItems, browserInterim, ownerId]);

  // RU item 3 (2026-10-20): recordings set aside by the resume card's "Not
  // now", keyed by this provider's owner and the exact queue id. React state
  // only (a new page, a remounted provider or another owner starts empty). A
  // recording that leaves the stack (saved, removed, merged into an inline
  // offer) simply stops matching; one of another owner never matches.
  const [deferredKeys, setDeferredKeys] = useState<
    ReadonlyArray<{ ownerId: string; queueItemId: string; wordsMoved: boolean }>
  >([]);
  const deferredKeyOf = useCallback(
    (session: InterruptedSession) =>
      ownerId && session.mode === 'enhanced'
        ? deferredKeys.find((key) => key.ownerId === ownerId && key.queueItemId === session.queueItemId) ?? null
        : null,
    [deferredKeys, ownerId],
  );
  const interruptedSessions = useMemo(
    () => allInterruptedSessions.filter((session) => deferredKeyOf(session) === null),
    [allInterruptedSessions, deferredKeyOf],
  );
  const deferredInterruptions = useMemo(
    () => allInterruptedSessions.flatMap((session) => {
      const key = deferredKeyOf(session);
      return key ? [{ session, wordsMoved: key.wordsMoved }] : [];
    }),
    [allInterruptedSessions, deferredKeyOf],
  );
  const deferInterruption = useCallback((sessionId: string, options?: { wordsMoved?: boolean }): boolean => {
    if (!ownerId) return false;
    const session = allInterruptedSessions.find((entry) => entry.id === sessionId);
    if (!session || session.mode !== 'enhanced' || !session.queueItemId) return false;
    const queueItemId = session.queueItemId;
    const wordsMoved = options?.wordsMoved === true;
    setDeferredKeys((keys) =>
      keys.some((key) => key.ownerId === ownerId && key.queueItemId === queueItemId)
        ? keys
        : [...keys, { ownerId, queueItemId, wordsMoved }],
    );
    return true;
  }, [allInterruptedSessions, ownerId]);

  const dismissInterruption = useCallback(async (): Promise<boolean> => {
    // Core-content cutover: dismissing an interruption DESTROYS recovery
    // material. The enhanced branch drops a queue row plus its audio
    // bytes, which may be the only copy of that prayer; the browser
    // branch removes the preservation-set key adia-interrupted-interim.
    // Neither happens while the read-only gate is active. The resume card is not rendered
    // while read-only (PrayPage), so nothing calls this; the guard is
    // the invariant behind that render gate, not a substitute for it.
    if (isCoreReadOnly()) return false;
    // Pop the head. Enhanced: dequeue audio (IDB delete + store
    // remove). Browser: clear localStorage + state. A queue row is removed
    // only while it is still the recording this card offered (no Prayer
    // linked by another page's "Save what I have", not awaiting an inline
    // save, a new-prayer recording, unresolved); a stale card is refused at
    // the mutation boundary and leaves (discardInterruptedRecording).
    const head = interruptedSessions[0];
    if (!head) return false;
    if (head.mode === 'enhanced') {
      return discardInterruptedRecording(head.queueItemId);
    } else {
      if (head.queueItemId) return discardInterruptedRecording(head.queueItemId);
      if (!clearBrowserInterruptedInterim()) return false;
      setBrowserInterim(null);
      return true;
    }
  }, [interruptedSessions]);

  // ---- V.1.1 inline-save rescue API ----
  // Reactive selector — true whenever any queue row carries the
  // awaitingInlineSave flag. Drives PrayPage's Save-button gate so
  // the button stays visible when the user has no typed text but a
  // transcribe-fail-on-stop orphan is parked in IDB. Survives reload
  // because the orphan is on disk.
  // Positive allowlist (bookmarks Phase 2): only PRAYER-source orphans
  // are claimable into a Prayer. normalizeSource maps absent/legacy to
  // 'prayer'; bookmark and any future/malformed value are excluded. The same
  // selection (audio-queue.ts homeInlineSaveOffer, RU item 2: a recording
  // with a valid Bible passage too) chooses what Home offers, what its Save
  // claims and what its Discard removes.
  const inlineSaveAvailable = useAudioQueueStore((s) => homeInlineSaveOffer(s.items) !== null);
  const inlineSaveSavable = useAudioQueueStore((s) => {
    const offer = homeInlineSaveOffer(s.items);
    return offer !== null && !inlineSaveOfferFailedWithoutWords(offer.item);
  });

  const claimPendingInlineSave = useCallback(
    async (
      scriptureAnchor?: ScriptureAnchor,
    ): Promise<{ queueItemId: string } | null> => {
      const expectedAnchor = scriptureAnchor
        ? parseScriptureAnchor(scriptureAnchor)
        : null;
      if (scriptureAnchor && !expectedAnchor) return null;
      // Read from the in-memory store mirror — hydrated at boot by
      // hydrateQueueStore, safe to read by handleSave time. Most-
      // recent first matches the resume card's stack semantics.
      const items = useAudioQueueStore.getState().items;
      // Positive allowlist (bookmarks Phase 2): claim ONLY prayer-source
      // orphans, so a bookmark recording can never become a Prayer. With no
      // passage this is the claim of a Save right after a recording made on
      // Home (that recording has no passage); when Home's offer is why Save is
      // shown, PrayPage claims the offer itself (homeInlineSaveOffer, RU item
      // 2), the recording its Discard removes.
      const head = newestInlineSaveOffer(items, 'prayer', expectedAnchor);
      // Mic repair F2: a recording in which no words were heard is never
      // claimed into a prayer (it would stay empty: the server does not
      // transcribe a failed record again). It stays offered for Discard.
      if (!head || inlineSaveOfferFailedWithoutWords(head)) return null;
      // Don't flip the flag here. createPendingPrayerFromAudio flips it
      // atomically when PrayPage binds the queue item to the new Prayer.
      // Until then the row stays awaiting-inline-save so
      // the processor still skips it and the resume card still
      // hides it.
      return { queueItemId: head.id };
    },
    [],
  );

  const clearPendingInlineSave = useCallback(
    async (source: 'prayer' | 'bookmark'): Promise<string[]> => {
      if (source === 'prayer') {
        return discardLatestAwaitingInlineSaveOrphan('prayer');
      }
      return clearAwaitingInlineSaveOrphans('bookmark');
    },
    [],
  );

  // One finalizer owns a session. Visibility/pagehide and an account switch
  // can arrive together, so both paths join this exact persistence promise.
  // The provider receives the owner that passed OwnerGate as an immutable prop;
  // it can therefore claim MediaRecorder synchronously before any auth/IDB await
  // even when the Supabase session has already changed to the next account.
  const persistVoiceInterruption = useCallback(async (
    deferProcessing: boolean,
  ): Promise<void> => {
    if (isCoreReadOnly() || !hasActiveVoiceCaptureWork()) return;
    if (!ownerId) throw new Error('voice-interruption-owner-missing');

    const options = activeStartOptionsRef.current;
    const recoveryDestination = options?.recoveryDestination;
    const inlineSaveSource = options?.inlineSaveSource ?? 'prayer';
    const bookmarkOrphan = !recoveryDestination && inlineSaveSource === 'bookmark';
    const interimAtInterruption = interimRef.current;
    const activeTarget = targetRef.current;
    const targetRecoveryText = activeTarget
      ? targetsRef.current.get(activeTarget)?.getRecoveryText?.() ?? ''
      : '';

    // Calling, rather than awaiting, is the synchronous ownership handoff.
    // It rebinds MediaRecorder.onstop before React can unmount this provider.
    const finalization = finalizeForInterruption();
    const result = await finalization;
    if (!result) {
      // A normal stop may already own the recorder and be between its data event
      // and durable queue commit. Join that promise before releasing the drain.
      assertVoiceStopDurable(await stopListening());
      return;
    }

    // The interruption claimed this capture; its custody commits the bytes
    // (or words) to their own id through db.commitCaptureRow (plan rev 4.1,
    // section 4.2), with the posture today's interruption gives them.
    const commitInterruption = async (custodyId: string): Promise<void> => {
      const committed = await commitCapture(custodyId, 'interruption', {
        expectedOwnerId: ownerId,
        awaitingInlineSave: bookmarkOrphan || undefined,
        inlineSaveSource,
        deferProcessing,
        continuePipeline: false,
        publishToList: true,
      });
      if (committed.kind === 'failed') throw committed.error;
      if (committed.kind !== 'durable') throw new Error('voice-interruption-not-durable');
    };
    if (result.mode === 'enhanced') {
      if (result.ownerId !== ownerId) {
        throw new Error('voice-interruption-owner-changed');
      }
      await commitInterruption(result.queueId);
      return;
    }

    const capturedText = [
      targetRecoveryText.trim(),
      interimAtInterruption.trim(),
    ].filter(Boolean).join(' ');
    if (
      result.ownerId === ownerId &&
      typeof result.authEpoch === 'number' &&
      result.queueId &&
      result.destinationId &&
      result.sourceDeviceId
    ) {
      if (!capturedText) {
        endCaptureWithoutPayload(result.queueId);
        return;
      }
      deliverCaptureText(result.queueId, capturedText);
      await commitInterruption(result.queueId);
      return;
    }
    if (!capturedText) return;
    const persisted = saveBrowserInterruptedInterim(
      capturedText,
      options?.scriptureAnchor,
    );
    if (!persisted) throw new Error('voice-browser-interruption-persist-failed');
    setBrowserInterim(persisted);
  }, [finalizeForInterruption, ownerId, stopListening]);

  const ensureVoiceInterruptionPersisted = useCallback((
    deferProcessing: boolean,
  ): Promise<void> => {
    if (interruptionPersistenceRef.current) {
      return interruptionPersistenceRef.current;
    }
    // A latched failure stands only while its capture is still unsaved: once a
    // retry made every capture durable, nothing is left to refuse for.
    if (interruptionPersistenceFailureRef.current && unsavedRecordingState().failed === 0) {
      interruptionPersistenceFailureRef.current = null;
    }
    if (interruptionPersistenceFailureRef.current) {
      return Promise.reject(interruptionPersistenceFailureRef.current);
    }

    // The capture this persistence takes over, read before any await.
    const record = captureRef.current && !captureRef.current.settled
      ? captureRef.current
      : null;
    const tracked = persistVoiceInterruption(deferProcessing)
      .then(() => {
        interruptionPersistenceFailureRef.current = null;
        if (record) finishCapture(record, { durability: 'committed' });
      })
      .catch((error: unknown) => {
        const failure = error instanceof Error
          ? error
          : new Error('voice-interruption-persist-failed');
        interruptionPersistenceFailureRef.current = failure;
        if (record) finishCapture(record, { durability: 'failed', error: failure });
        throw failure;
      })
      .finally(() => {
        if (interruptionPersistenceRef.current === tracked) {
          interruptionPersistenceRef.current = null;
          setInterruptionInFlight(false);
        }
      });
    interruptionPersistenceRef.current = tracked;
    setInterruptionInFlight(true);
    return tracked;
  }, [persistVoiceInterruption, finishCapture]);

  // ---- Visibility / pagehide handler (B.2 lifecycle) ----
  useEffect(() => {
    const persistHidden = () => {
      void ensureVoiceInterruptionPersisted(false).catch((error) => {
        console.error('[Adia] interruption persistence failed', (error as Error).name);
      });
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') persistHidden();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', persistHidden);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', persistHidden);
    };
  }, [ensureVoiceInterruptionPersisted]);

  // A browser-wide owner replacement waits for this same promise before its
  // shared runtime lock releases. A rejected persistence remains latched and
  // aborts replacement, preserving the old workspace for recovery.
  const drainForOwnerTransition = useCallback(async (): Promise<void> => {
    if (isCoreReadOnly() || !hasActiveVoiceCaptureWork()) return;
    await ensureVoiceInterruptionPersisted(true);
  }, [ensureVoiceInterruptionPersisted]);

  useEffect(
    () => registerOwnerTransitionDrain(drainForOwnerTransition),
    [drainForOwnerTransition],
  );

  // R4 admission, synchronous and read from refs only. Refused while a capture
  // is starting, recording or still settling (its stop, upload, transcription,
  // delivery and this provider's own completion), while an interruption or
  // owner-replacement persistence is in flight, or behind the owner barrier.
  const isAdmissionBusy = useCallback((): boolean => {
    const record = captureRef.current;
    return Boolean(record && !record.settled) ||
      getCaptureActivity().busy ||
      interruptionPersistenceRef.current !== null ||
      // Plan rev 4.1, section 5.1: any capture still in custody (starting,
      // recording, stopping, unresolved, failed, or with outstanding work).
      isCustodyAdmissionBusy() ||
      isOwnerTransitionBarrierActive();
  }, [getCaptureActivity]);

  const [, setRefusalTick] = useState(0);
  const refuseStart = useCallback(() => {
    refusalCountRef.current += 1;
    setRefusalTick(refusalCountRef.current);
  }, []);

  const beginListening = useCallback(
    (
      name: string | null,
      opts?: SpeechStartOptions,
    ): boolean => {
      if (!isSupported) return false;
      // Admission before any state changes: a refused start touches no
      // target, start options, error or active-work marker.
      if (isAdmissionBusy()) {
        refuseStart();
        return false;
      }
      const acceptedOptions = snapshotStartOptions(opts);
      const speechSeq = startListening(acceptedOptions);
      if (speechSeq === null) {
        refuseStart();
        return false;
      }
      interruptionPersistenceFailureRef.current = null;
      captureIdRef.current += 1;
      captureRef.current = {
        id: captureIdRef.current,
        target: name,
        token: acceptedOptions?.captureToken,
        speechSeq,
        settled: false,
        cancelled: false,
      };
      setCaptureBusy(true);
      activeStartOptionsRef.current = acceptedOptions;
      if (name) {
        targetRef.current = name;
        setTargetState(name);
      }
      setInterim('');
      // Clear any stale error toast left over from a prior failed
      // session so the new recording starts with a clean surface.
      clearError();
      return true;
    },
    [isSupported, isAdmissionBusy, refuseStart, startListening, clearError]
  );

  const start = useCallback(
    (name?: string, opts?: SpeechStartOptions): boolean => {
      // Core-content cutover: no capture at all, and no disclosure modal
      // for a mic that cannot record. useSpeechToText.startListening
      // carries the same guard as the deeper backstop.
      if (isCoreReadOnly() || !isSupported) return false;
      if (isAdmissionBusy()) {
        refuseStart();
        return false;
      }
      // First-tap gate: show the disclosure once before any recording ever
      // begins. Captures the target AND opts (bookmarks Phase 2) so the
      // confirm path routes to the same surface with the same source.
      if (!hasSeenVoiceDisclosure()) {
        pendingStartRef.current = {
          name: name ?? null,
          opts: snapshotStartOptions(opts),
        };
        setDisclosureOpen(true);
        return true;
      }
      return beginListening(name ?? null, opts);
    },
    [beginListening, isAdmissionBusy, isSupported, refuseStart]
  );

  const handleDisclosureConfirm = useCallback(() => {
    const pending = pendingStartRef.current;
    // A dismissed request cannot start an untargeted capture through a late
    // confirmation callback.
    if (!pending) return;
    markVoiceDisclosureSeen();
    setDisclosureOpen(false);
    pendingStartRef.current = null;
    // beginListening re-checks admission, so a capture that became active
    // while the disclosure was open is never interleaved.
    beginListening(pending.name, pending.opts);
  }, [beginListening]);

  const handleDisclosureDismiss = useCallback(() => {
    // "Seen once" is enough — dismissing still counts. The user had the
    // information in front of them; starting the mic later shouldn't
    // replay the modal every time.
    markVoiceDisclosureSeen();
    setDisclosureOpen(false);
    pendingStartRef.current = null;
  }, []);

  const cancelPendingStart = useCallback((name: string, captureToken: string): boolean => {
    const pending = pendingStartRef.current;
    if (!name || !captureToken || !pending || pending.name !== name ||
      pending.opts?.captureToken !== captureToken) return false;
    handleDisclosureDismiss();
    return true;
  }, [handleDisclosureDismiss]);

  // Each continuation below acts only on the capture it was issued for
  // (finishCapture checks identity), so a late continuation of an earlier
  // capture can never clear a newer capture's state.
  const stop = useCallback(async (): Promise<void> => {
    // Clear interim immediately — no more streaming text from this session.
    // Keep the target set until stopListening resolves so enhanced-mode
    // transcription can still route its onFinal result to the registered
    // handler. Only clear the target once the stop has completed.
    setInterim('');
    const record = captureRef.current;
    const result = await stopListening();
    if (record) {
      finishCapture(record, result);
    } else if (!captureRef.current) {
      // No capture was ever accepted by this provider: today's behavior.
      if (result.durability === 'failed') {
        interruptionPersistenceFailureRef.current = result.error;
      }
      activeStartOptionsRef.current = undefined;
      targetRef.current = null;
      setTargetState(null);
    }
    assertVoiceStopDurable(result);
  }, [stopListening, finishCapture]);

  const cancel = useCallback(async (): Promise<void> => {
    // Drop any in-flight audio without transcribing. Used by "clear" paths
    // where the user has abandoned the recording.
    setInterim('');
    const record = captureRef.current && !captureRef.current.settled
      ? captureRef.current
      : null;
    if (record) record.cancelled = true;
    activeStartOptionsRef.current = undefined;
    targetRef.current = null;
    setTargetState(null);
    let result: VoiceStopResult | null = null;
    try {
      result = await cancelListening();
    } finally {
      if (record) finishCapture(record, result);
    }
  }, [cancelListening, finishCapture]);

  const registerTarget = useCallback(
    (name: string, handlers: TargetHandlers) => {
      targetsRef.current.set(name, handlers);
      return () => {
        targetsRef.current.delete(name);
        // During provider teardown the capture belongs to the teardown
        // claimant (plan rev 4.1, section 3.3): an editor's clean-up joins it
        // instead of issuing a second stop.
        if (tearingDownRef.current) return;
        if (targetRef.current === name) {
          const record = captureRef.current;
          void stopListening()
            .then((result) => {
              if (record) {
                finishCapture(record, result);
                return;
              }
              if (result.durability === 'failed') {
                interruptionPersistenceFailureRef.current = result.error;
              }
              activeStartOptionsRef.current = undefined;
            })
            .catch((error: unknown) => {
              interruptionPersistenceFailureRef.current = error instanceof Error
                ? error
                : new Error('voice-stop-failed');
            });
          setInterim('');
          targetRef.current = null;
          setTargetState(null);
        }
      };
    },
    [stopListening, finishCapture]
  );

  const getCaptureState = useCallback((): VoiceCaptureState => {
    const record = captureRef.current;
    const live = record && !record.settled ? record : null;
    const activity = getCaptureActivity();
    return {
      target: live?.target ?? null,
      captureToken: live?.token,
      listening: Boolean(live) && activity.listening,
      stopping: Boolean(live) &&
        (activity.stopping || interruptionPersistenceRef.current !== null),
    };
  }, [getCaptureActivity]);

  const getRefusalCount = useCallback(() => refusalCountRef.current, []);

  useEffect(() => {
    targetRef.current = target;
  }, [target]);

  // Custody across this provider's lifetime (plan rev 4.1, sections 3.3 and
  // 3.6). Declared after useSpeechToText, so on unmount its teardown claim has
  // already run: this clean-up hands a browser capture the words this page
  // still holds (finalized text plus the live interim) and retires every
  // capture this instance admitted. A genuine new instance runs one automatic
  // retry of its owner's failed captures; StrictMode's replay reuses the key.
  useEffect(() => {
    tearingDownRef.current = false;
    // The registered targets live in one Map for the provider's lifetime.
    const targets = targetsRef.current;
    if (ownerId) void retryFailedCaptures(`mount:${custodyRuntimeId}`, ownerId);
    return () => {
      tearingDownRef.current = true;
      const pending = browserCapturesAwaitingText(custodyRuntimeId);
      if (pending.length > 0) {
        const activeTarget = targetRef.current;
        let targetText = '';
        try {
          targetText = activeTarget
            ? targets.get(activeTarget)?.getRecoveryText?.() ?? ''
            : '';
        } catch {
          targetText = '';
        }
        const words = [targetText.trim(), interimRef.current.trim()].filter(Boolean).join(' ');
        for (const custodyId of pending) deliverCaptureText(custodyId, words);
      }
      retireRuntimeCaptures(custodyRuntimeId);
    };
  }, [custodyRuntimeId, ownerId]);

  const isStopping = speechIsStopping || interruptionInFlight;
  const admissionBusy = captureBusy || isStopping || custodyBusy;

  const value = useMemo<VoiceCaptureValue>(
    () => ({
      target,
      setTarget,
      interim,
      isRecording: isListening,
      isTranscribing,
      isSupported,
      isStopping,
      admissionBusy,
      isAdmissionBusy,
      getRefusalCount,
      getCaptureState,
      start,
      cancelPendingStart,
      stop,
      cancel,
      registerTarget,
      getLevel,
      interruptedSessions,
      deferredInterruptions,
      deferInterruption,
      dismissInterruption,
      inlineSaveAvailable,
      inlineSaveSavable,
      claimPendingInlineSave,
      clearPendingInlineSave,
    }),
    [target, setTarget, interim, isListening, isTranscribing, isSupported, isStopping, admissionBusy, isAdmissionBusy, getRefusalCount, getCaptureState, start, cancelPendingStart, stop, cancel, registerTarget, getLevel, interruptedSessions, deferredInterruptions, deferInterruption, dismissInterruption, inlineSaveAvailable, inlineSaveSavable, claimPendingInlineSave, clearPendingInlineSave]
  );

  return (
    <VoiceCaptureContext.Provider value={value}>
      {children}
      {disclosureOpen && (
        <VoiceDisclosure
          onConfirm={handleDisclosureConfirm}
          onDismiss={handleDisclosureDismiss}
        />
      )}
      {/* Surface any voice-capture error that useSpeechToText flagged so the
          user isn't left wondering why no text showed up. Covers 502 after
          all retries, permission denials, empty transcriptions, and
          recorder-construction failures. Auto-dismisses after 5s. */}
      {error && <VoiceErrorToast error={error} onDismiss={clearError} />}
      {/* V.A.3: post-transcription notice when a recording auto-stopped at
          the 10-minute cap. Self-mounted (no props) — listens to a window
          event dispatched from useSpeechToText's onstop success branch.
          Mounted here so all five voice-capture surfaces share one mount. */}
      <AutoStopToast />
      {/* A tap that could start no transcription says why (rule #21), and
          clears a stale error toast at the same spot when it opens. */}
      <VoiceStartUnavailableToast onShow={clearError} />
    </VoiceCaptureContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useVoiceCapture(): VoiceCaptureValue {
  const ctx = useContext(VoiceCaptureContext);
  if (!ctx) {
    throw new Error('useVoiceCapture must be used within a VoiceCaptureProvider');
  }
  return ctx;
}
